import Logo from '@client/assets/images/nodon-logo-gray.svg';
import React, { FC, useMemo } from 'react';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import {
  Report,
  useGetScalingSize,
  REPORT_BORDER_WIDTH_PERCENT,
} from '@ui/Report';
import { EllipsisText } from '@ui/EllipsisText';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { guards } from '@shared/helpers/typeguards.helpers';

type ReportViewFooterProps = {
  organizationName?: string;
  projectName?: string;
  /** Version name in the third slot. */
  contextName?: string;
  gfa?: number;
};

export const ReportViewFooter: FC<ReportViewFooterProps> = ({
  organizationName,
  projectName,
  contextName,
  gfa,
}) => {
  const currentDate = useMemo(() => DateTime.now().toFormat('yyyy-MM-dd'), []);
  const getScalingSize = useGetScalingSize();

  const items: React.ReactNode[] = [];
  if (organizationName) {
    items.push(<FooterItem key="org">{organizationName}</FooterItem>);
  }
  if (projectName) {
    items.push(
      <FooterItem key="project">
        <Box
          sx={{
            display: 'inline-flex',
            maxWidth: getScalingSize(20),
          }}
        >
          <EllipsisText verticalAlign="middle">{projectName}</EllipsisText>
        </Box>
      </FooterItem>,
    );
  }
  if (contextName) {
    items.push(<FooterItem key="context">{contextName}</FooterItem>);
  }
  if (gfa !== undefined) {
    items.push(
      <FooterItem key="gfa">
        {guards.is.string(gfa) ? gfa : getDisplayNumber(gfa, { decimals: 0 })}{' '}
        m²
        <Report.Text
          as="span"
          fontSize={0.65}
          sx={{
            display: 'inline-block',
            verticalAlign: 'baseline',
            transform: `translateY(${getScalingSize(0.2)})`,
          }}
        >
          GFA
        </Report.Text>
      </FooterItem>,
    );
  }
  items.push(<FooterItem key="date">{currentDate}</FooterItem>);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: getScalingSize(0.6),
        }}
      >
        {items.flatMap((item, index) =>
          index === 0
            ? [item]
            : [<FooterSeparator key={`sep-${index}`} />, item],
        )}
      </Box>
      <img
        alt="nodon logo"
        src={Logo}
        style={{ height: getScalingSize(1.8), marginLeft: 'auto' }}
      />
    </Box>
  );
};

const FooterItem: FC<{ children: React.ReactNode }> = ({ children }) => {
  return <Report.Text as="span">{children}</Report.Text>;
};
const FooterSeparator: FC = () => {
  const getScalingSize = useGetScalingSize();
  return (
    <Box
      sx={{
        width: getScalingSize(REPORT_BORDER_WIDTH_PERCENT),
        height: getScalingSize(1.2),
        borderRight: 1,
        borderColor: 'divider',
      }}
    />
  );
};
